import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { RootState } from 'appHooks'
import { format } from 'date-fns'
import { IKeyFiguresAccounts } from 'shared/models/KeyFigures'

type BankAccountState = {
  selectedBankAccount: IKeyFiguresAccounts | null
  filters: {
    year: number
    period?: number
    maxAmount?: number
    minAmount?: number
    searchText?: string
  }
  openAccordions: number[]
}

const currentPeriod = () => Number(format(new Date(), 'yyyyMM'))

const initialState: BankAccountState = {
  selectedBankAccount: null,
  filters: {
    year: new Date().getFullYear(),
    period: Number(new Date().getFullYear() + '01'),
    maxAmount: undefined,
    minAmount: undefined,
    searchText: '',
  },
  openAccordions: [currentPeriod()],
}

const bankAccountsSlice = createSlice({
  name: 'bankAccounts',
  initialState,
  reducers: {
    setSelectedBankAccount: (
      state,
      action: PayloadAction<IKeyFiguresAccounts | null>
    ) => {
      state.selectedBankAccount = action.payload
    },
    setYear: (state, action: PayloadAction<number>) => {
      state.filters.year = action.payload
    },
    setPeriod: (state, action: PayloadAction<number>) => {
      state.filters.period = action.payload
    },
    setMaxAmount: (state, action: PayloadAction<number>) => {
      state.filters.maxAmount = action.payload
    },
    setMinAmount: (state, action: PayloadAction<number>) => {
      state.filters.minAmount = action.payload
    },
    setBankAccountFilters: (
      state,
      action: PayloadAction<BankAccountState['filters']>
    ) => {
      state.filters = action.payload
    },
    setSearchText: (state, action: PayloadAction<string>) => {
      state.filters.searchText = action.payload
    },
    addOpenAccordion: (state, action: PayloadAction<number>) => {
      if (!state.openAccordions.includes(action.payload)) {
        state.openAccordions.push(action.payload)
      }
    },
    removeOpenAccordion: (state, action: PayloadAction<number>) => {
      state.openAccordions = state.openAccordions.filter(
        (id) => id !== action.payload
      )
    },
  },
})

export const {
  setSelectedBankAccount,
  setYear,
  setPeriod,
  setMaxAmount,
  setMinAmount,
  setBankAccountFilters,
  setSearchText,
  addOpenAccordion,
  removeOpenAccordion,
} = bankAccountsSlice.actions

export default bankAccountsSlice

export const getSelectedBankAccount = (state: RootState) =>
  state.bankAccounts.selectedBankAccount

export const getYear = (state: RootState) => state.bankAccounts.filters.year

export const getPeriod = (state: RootState) => state.bankAccounts.filters.period

export const getMaxAmount = (state: RootState) =>
  state.bankAccounts.filters.maxAmount

export const getMinAmount = (state: RootState) =>
  state.bankAccounts.filters.minAmount

export const getFilters = (state: RootState) => state.bankAccounts.filters

export const getBankAccountFilters = (state: RootState) => {
  return state.bankAccounts.filters
}

export const getSearchText = (state: RootState) =>
  state.bankAccounts.filters.searchText

export const getOpenAccordions = (state: RootState) =>
  state.bankAccounts.openAccordions
